export const landingHeroContent = {
  title: "Custom Technology Solutions ",
  titleHighlight: "for Small to Midsize Nonprofits",
  subtitle:
    "Your nonprofit shouldn't have to choose between off-the-shelf software that doesn't work and enterprise solutions they can't afford. We're a tech consultancy that builds custom, affordable, and easy-to-use tools that fit your unique workflow.",
  ctaButton: {
    label: "Schedule a Brainstorming Session",
    ctaUrl: "/professional-services",
  },
};

export const cookieContent = {
  title: "We respect your Privacy",
  description:
    "We use cookies to enhance your browsing experience, analyze site traffic, and serve targeted content. By continuing to use our site, you consent to our use of cookies. View our privacy policy.",
  buttons: {
    accept: "Accept",
    reject: "Reject",
  },
};

export const featuresContent = {
  title: "At Grantyd, Our Mission Is To",
  titleHighlight: "Help You Get Back to Yours",
  ctaUrl: "/professional-services",
  description:
    "Our projects typically fall into four main categories, each of which are customized to fit your needs.",
  features: [
    {
      title: "1. Custom Tools & Solutions",
      subtitle: "Technology custom built for your organization.",
      description:
        "When off-the-shelf software doesn't meet your unique needs, we build a solution that's perfectly tailored to your process. Get the exact functionality you need without paying for features you don't.",
      bullets: [
        "Apps for Google Workspace & Microsoft 365: We create simple tools that live right inside the software you already use every day.",
        "Custom Grant & Program Management: We design and build systems that track grants, clients, or program activities exactly the way you work.",
        "Tailored Databases & CRMs: Move beyond complicated spreadsheets with a simple, custom system for managing your donors, volunteers, or members.",
      ],
      dark: true,
    },
    {
      title: "2. Data Insights & Reporting",
      subtitle:
        "Eliminate manual reporting and begin to understand your metrics and program KPIs effortlessly.",
      description:
        "We transform your scattered program, donor, and operational data into clear, actionable insights. Make confident, data-driven decisions to measure your impact, secure funding, and guide your mission.",
      bullets: [
        "Automated Dashboards & Visualizations: See your most important metrics in real-time with easy-to-understand charts and graphs.",
        "Data Cleaning & Enrichment: We organize and clean your existing data so you can finally trust it for accurate reporting and analysis.",
        "Custom Reports & Analysis: We answer your most pressing questions by analyzing your program outcomes, survey feedback, or donor trends.",
      ],
      dark: true,
    },
    {
      title: "3. Systems Integration & Automation",
      subtitle: 'Never hear "Our systems don\'t talk to eachother" ever again.',
      description:
        "We connect your various software tools and automate your repetitive manual tasks. This frees up your team's valuable time to focus on strategic work, not data entry.",
      bullets: [
        "Connecting Your Software: We make your separate systems—like your donation platform, email marketing, and accounting software—talk to each other seamlessly.",
        "Automated Workflows: We build simple automations to handle tasks like sending donor acknowledgements, updating contact lists, or generating weekly summaries.",
        "Process Efficiency: We help you identify and eliminate technical bottlenecks in your day-to-day operations.",
      ],
      dark: true,
    },
    {
      title: "4. Challenge Us",
      subtitle:
        "Got a 'what if...?' idea? We build the tech to make it a reality.",
      description:
        "Your mission is unique, and so are your challenges. This service is for your most ambitious ideas—the ones that don't fit in a box. Before you assume it's 'too complicated' or 'not possible for our budget,' talk to us. We specialize in creative, custom-fit solutions that others won't build.",
      bullets: [
        "Custom Slackbots & Integrations: We can build a bot that lets your team log donor interactions or create tasks right from a Slack conversation, posting it directly to your CRM.",
        "Advanced Impact Mapping: We'll combine your program participant addresses with US Census data to create powerful maps and reports that *visually prove* your impact to funders.",
        "AI-Powered Survey Analysis: Stop spending days reading open-ended survey comments. We use AI to analyze thousands of responses and instantly discover the key themes, trends, and sentiments.",
      ],
      dark: false,
    },
  ],
};

export const landingFeaturesContent = {
  title: "At Grantyd, Our Mission Is To",
  titleHighlight: "Help You Get Back To Yours",
  ctaUrl: "",
  description: "Equitable tech for small to midsize nonprofits.",
  features: [
    {
      title: "Funding Intelligence",
      subtitle: "Turn raw funding data into strategic insights",
      bullets: [
        "Transform messy spreadsheets",
        "Reveal trends in your fundraising",
        "Surface key metrics",
      ],
      href: import.meta.env.VITE_WEBSITE_URL + "/professional-services",
      dark: true,
    },
    {
      title: "Program Insights",
      subtitle: "Connect data to decision making",
      bullets: [
        "Track trends across operations",
        "Generate clear, actionable reports",
        "Visualize program outcomes and KPIs in one place",
      ],
      href: import.meta.env.VITE_WEBSITE_URL + "/professional-services",
      dark: false,
    },
  ],
};

export const faqContent = {
  title: "Frequently Asked Questions",
  faqs: [
    {
      question:
        "This sounds great, but we have a very limited budget. How does your pricing work?",
      answer:
        "We understand. Every dollar counts for a nonprofit, which is why we are committed to price transparency. Our focused, high-impact projects start as low as $2,000. We don't sell one-size-fits-all packages; our pricing is 100% project-based and tailored to your specific needs. We start every engagement with a free, no-obligation consultation to understand your goals. From there, we'll provide a clear, flat-fee proposal so you know the full cost upfront.",
    },
    {
      question:
        "We're a really small nonprofit with only a few staff. Are your services right for us?",
      answer:
        "Absolutely. In many ways, our services are built for you. Small teams feel the pain of manual tasks and messy data the most. We specialize in creating high-impact, low-maintenance solutions—like a simple automated report or connecting two key systems—that can give a small team back 10-15 hours a week. We're all about finding the 'quick wins' that make a huge difference for a small staff.",
    },
    {
      question:
        "We know our tech is a mess, but we don't even know where to start. Can you help?",
      answer:
        "This is one of the most common challenges we see, and it's the perfect place to start. Our Technology Strategy & Support service is designed for this exact situation. We can begin with a 'Technology Roadmap' or an 'Unbiased Software Audit.' We'll sit down with you, understand your mission, learn about your daily frustrations, and provide a clear, prioritized plan to move forward. You don't need to know the solution; you just need to know your pain points.",
    },
    {
      question: "What makes you different from a generic IT support company?",
      answer:
        "A generic IT company helps you when your printer breaks or your email is down. We act as your strategic partner to help you use technology to advance your mission. We're not just 'fix-it' people; we're nonprofit strategists, data analysts, and process builders who focus on outcomes: How can we help you raise more money? How can we prove your program's impact to a funder? How can we save your team from burnout?",
    },
    {
      question:
        "Our data is a total mess. It's in 10 different spreadsheets, our old donor system, and a few Google Forms. Can you actually fix that?",
      answer:
        "Yes. This is our specialty! Our Data Cleaning & Enrichment service is designed to tackle this exact problem. We'll help you consolidate, clean, and organize your scattered data into one trusted source. The goal isn't just to make it 'clean,' but to make it useful so you can finally run accurate reports for your board or a grant application.",
    },
    {
      question:
        "What's an 'automated dashboard,' and will my team actually be able to use it?",
      answer:
        "Think of a dashboard as a simple, one-page website that shows your most important numbers in real-time—like 'donations this month,' 'new volunteers,' or 'clients served.' Instead of you spending hours building a report, the dashboard updates itself automatically. We design them to be incredibly simple and visual (think charts and graphs), so even the least tech-savvy person on your team can look at it and understand your impact at a glance.",
    },
    {
      question:
        "What do you mean by 'making systems talk to each other'? Can you give an example?",
      answer:
        "A perfect example is connecting your donation platform (like Donorbox or Givebutter) to your email marketing tool (like Mailchimp or Constant Contact). Right now, you probably have to manually export a list of new donors and upload it to your email list. We build an 'integration' that does this for you automatically, in real-time. A new donor gives, and they are instantly added to the 'New Donor Welcome' email series. That's a Systems Integration.",
    },
    {
      question:
        "What's an 'automated workflow'? We're worried about automating too much.",
      answer:
        "An automated workflow isn't about removing the human touch; it's about removing the human error and drudgery. A great example is donor acknowledgments. Instead of you manually running a report and sending a thank-you, we can build a workflow that:\n1. Triggers when a new donation comes in.\n2. Instantly sends a personalized thank-you email and tax receipt.\n3. Creates a task in your calendar to make a personal follow-up call a week later if the donation is over $500.\n\nIt handles the admin work, so your team can focus on the high-value personal connection.",
    },
    {
      question:
        "When does a custom tool make more sense than just buying off-the-shelf software?",
      answer:
        "This is a key question. Off-the-shelf software is often a great start. But you should consider a custom tool when:\n You're paying for a massive, expensive system but only using 10% of its features.\n Your program has a unique workflow (like a specific client intake or grant tracking process) that no existing software can handle.\n You find yourself 'living in spreadsheets' because your main software is too clunky, and it's creating more manual work.\n\nOur Custom Tools are often small, simple, and built to do one thing perfectly, filling the gaps that other software leaves behind.",
    },
    {
      question: "'Custom' sounds expensive and complicated. Is it?",
      answer:
        "It's usually far less expensive and complicated than you think. A 'custom solution' doesn't have to be a massive, six-figure project. Often, it's a simple App for Google Workspace that lives inside your Google Sheets and automates your reporting. Or it's a clean, simple Custom CRM that costs a fraction of a big system and only includes the 5-6 features you actually need to manage your donors. We build for your budget.",
    },
    {
      question:
        "We need to buy a new volunteer management system, and we're overwhelmed by the options. Can you help?",
      answer:
        "Yes. This is a core part of our Unbiased Software Advice. We are technology-agnostic, meaning we don't get kickbacks for recommending one product over another. We'll sit with your team, map out your actual needs, and then help you vet the options. We can even sit in on demos with you and ask the tough technical questions. Our only goal is to help you find the right tool for your mission and budget, preventing a costly mistake.",
    },
    {
      question:
        "Something on our website (or database) broke, and our main program is today. Can you help us now?",
      answer:
        "Yes. Our On-Demand Technical Help is built for exactly these moments. Think of us as your on-call tech expert. You can reach out, and we'll prioritize troubleshooting the urgent problem to get you back up and running. While our main focus is long-term strategy, we're here to be your partner, and that means helping you put out fires when they happen.",
    },
  ],
};
export const grantStepsContent = {
  title: "",
  ctaUrl: "",
  titleHighlight: "",
  steps: [
    {
      title: "Extract Grants with One Click",
      feature:
        "No more endless copy-pasting. Pull questions directly from online portals into a clean, editable format.",
      buttonText: "Get Started",
    },
    {
      title: "Draft Smarter with AI or On Your Own",
      feature:
        "Let Grantyd's AI draft answers using your uploaded documents—or take the lead and write manually with everything organized in one place.",
      buttonText: "Get Started",
    },
    {
      title: "Auto-Fill & Submit in Minutes",
      feature:
        "When you're done, Grantyd auto-fills your answers back into the original grant portal—saving time and reducing errors.",
      buttonText: "Get Started",
    },
  ],
};

export const pricingContent = {
  title: "Pricing Plans That Scale With You",
  ctaUrl: "",
  subtitle: "Flexible tools for individuals, teams, and networks",
  billingOptions: {
    monthly: "Monthly",
    yearly: "Yearly (Save 15%)",
  },
  whatsIncluded: "What's Included?",
  featuresTitle: "Features",
  plans: [
    {
      id: "free",
      name: "Free",
      price: {
        monthly: 0,
        yearly: 0,
      },
      unit: "forever",
      buttonLabel: "Sign Up",
      description: "For solo users who need to extract and submit—fast.",
      features: [
        "Extract grant questions from portals",
        "Manually draft and organize responses",
        "Autofill responses back into online applications",
      ],
    },
    {
      id: "individual",
      name: "Individual",
      price: {
        monthly: 20,
        yearly: 240, // 20  12 = 240, with 15% discount = 204
      },
      unit: "month",
      buttonLabel: "Sign Up",
      description: "For solo users who want AI help and document support.",
      isPopular: true,
      features: [
        "Everything in Free",
        "Upload and organize documents",
        "Create boilerplates for reuse",
        "Use your documents for AI to autofill the first draft",
      ],
    },
    {
      id: "organization",
      name: "Organization",
      price: {
        monthly: 0,
        yearly: 0,
      },
      unit: "",
      buttonLabel: "Contact Us",
      description:
        "Ideal for nonprofits managing multiple users, grants, and deadlines.",
      features: [
        "All Individual features",
        "Shared document access",
        "Commenting and tagging on specific questions",
        "Unlimited view-only access to proposals for external partners",
      ],
    },
    {
      id: "federation",
      name: "Consultancy / Federation",
      price: {
        monthly: 0,
        yearly: 0,
      },
      unit: "",
      buttonLabel: "Contact Us",
      description: "For fiscal sponsors, consultants, and intermediaries.",
      features: [
        "All Organization features",
        "Organize and manage multiple org accounts from one dashboard",
        "Keep each org's documents and responses private to them",
      ],
    },
  ],
  popularTagText: "Most Popular",
};

export const partnersContent = {
  title: "Technology should fit your organization, not the other way around.",
  subtitle:
    "We know you're wearing multiple hats. Your day is split between fundraising, managing volunteers, running programs, and reporting to your board. The last thing you have time for is a clunky database or a spreadsheet that's grown into a monster.",
  ctaBox: {
    title: "Stop trying to fit your mission into a tech box.",
    description:
      "We build custom donor, volunteer, and program software that's built around you. Affordable tech for small NPOs.",
    buttonLabel: "Get Started",
  },
};
